import * as React from 'react'
import DialogTitle from '@mui/material/DialogTitle'
import Button from '@mui/material/Button'

import Grid from '@mui/material/Grid'

import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import Dialog from '@mui/material/Dialog'
import LoadingButton from '@mui/lab/LoadingButton'

import Divider from '@mui/material/Divider'
import { renderImportOptionsMenu } from './SubImportMenuItem'

import Stepper from '@mui/material/Stepper'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import StepButton from '@mui/material/StepButton'

import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'

import ArrowForwardIcon from '@mui/icons-material/ArrowForwardOutlined'
import EditableSelect from '../EditableSelect'
import TextField from '@mui/material/TextField'

import {
    useContextsStore,
    selectOwnedContexts,
} from '../../stores/useContextsStore'
import { UserContext } from '../../UserContext'

import { useTheme } from '@mui/material/styles'

import { SubImportContext } from './SubImportContext'
import { DialogsContext } from '../../DialogsContext'

import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import FormHelperText from '@mui/material/FormHelperText'
import FormControlLabel from '@mui/material/FormControlLabel'

import Switch from '@mui/material/Switch'

import axios from 'axios'
import qs from 'qs'

import { slugifyString, convertGPTResponsesToArray } from '../../utils/index'
import { generateTextImportItems } from './TextImportItems'
import ImportingNotice from './ImportingNotice'
import { chipHoverAccent } from '../../theme'
import { importErrorMessage } from './importError'

const steps = ['Start', 'Sources', 'Settings', 'Results']

export default function AiGraphImportDialog({
    presetContext = '',
    presetSearchString = '',
    open,
    onClose,
}) {
    const theme = useTheme() // hook to get the current theme

    const {
        activeSubImportDialog,
        setActiveSubImportDialog,
    } = React.useContext(SubImportContext)

    const {
        activeImportDialog,
        setActiveImportDialog,
        backLinkDialog,
        openImportDialog,
        closeImportDialog,
        setPresetSearchString,
    } = React.useContext(DialogsContext)

    // Shared across all import sources so the typed query persists when switching
    const searchString = presetSearchString
    const setSearchString = setPresetSearchString

    const [selectedContext, setSelectedContext] = React.useState(presetContext)

    const [errorMessage, setErrorMessage] = React.useState(null)

    const [importError, setImportError] = React.useState(null)

    const [completedSteps, setCompletedSteps] = React.useState([])

    const [visitedContext, setVisitedContext] = React.useState(false)

    const [numberOfResults, setNumberOfResults] = React.useState('10')

    const [modelToUse, setModelToUse] = React.useState('claude-opus-5-5')

    // Which kind of AI graph to build: 'llm graph' (AI-Generated Statements)
    // or 'ontology graph' (Reasoning Ontology Graph)
    const [aiQueryType, setAiQueryType] = React.useState('ontology graph')

    const [hideSearchTerms, setHideSearchTerms] = React.useState(true)

    const [fromInsideContext, setFromInsideContext] = React.useState(false)

    const [advancedSettings, setAdvancedSettings] = React.useState(false)

    const [modifyAnalyzedText, setModifyAnalyzedText] = React.useState('none')

    const [replaceEntities, setReplaceEntities] = React.useState(false)

    const menuItems = generateTextImportItems()

    const filteredMenuItems = menuItems.filter(
        ({ textInput }) =>
            textInput.includes('aiGraphs') ||
            textInput.includes('aiTopics') ||
            textInput.includes('aiOntologies') ||
            textInput.includes(backLinkDialog)
    )

    const { username } = React.useContext(UserContext)
    const contexts = useContextsStore(selectOwnedContexts)
    const contextNames = contexts.map(c => c.name)

    const [activeStep, setActiveStep] = React.useState(2)
    const [skipped, setSkipped] = React.useState(new Set())

    const handleNext = () => {
        let newSkipped = skipped

        setActiveStep(prevActiveStep => prevActiveStep + 1)
        setSkipped(newSkipped)
    }

    const handleBack = () => {
        setActiveStep(prevActiveStep => prevActiveStep - 1)
    }

    const handleStep = step => {
        setActiveStep(step)
    }

    const handleNumberOfResultsChange = event => {
        setNumberOfResults(event.target.value)
    }

    const handleSearchStringChange = event => {
        setSearchString(event.target.value)
    }

    const handleSearchLanguageChange = event => {
        setSearchLanguage(event.target.value)
    }

    const handleSearchCountryChange = event => {
        setSearchCountry(event.target.value)
    }

    React.useEffect(() => {
        if (activeSubImportDialog.type) {
            setActiveStep(2)
            setActiveImportDialog(activeSubImportDialog.type)
        }
    }, [activeSubImportDialog])

    React.useEffect(() => {
        switch (activeStep) {
            case 0: // Start
                setActiveSubImportDialog(false)
                setActiveImportDialog('import')
                break
            case 1: // Sources
                setActiveSubImportDialog(false)
                setActiveImportDialog('aiImport')
                break
            case 2: // Settings
                if (!presetContext) {
                    setSelectedContext(generatePresetContext(searchString))
                }
                break
            case 3: // Analysis
                if (!searchString) {
                    setErrorMessage(
                        `Please, provide the content you'd like to generate an ontology for.`
                    )
                    setActiveStep(2)
                    break
                }
                if (!selectedContext) {
                    setErrorMessage(
                        'Please, select the graph to import your data to.'
                    )
                    setActiveStep(2)
                    break
                }
                if (selectedContext) {
                    setCompletedSteps([...completedSteps, 3])
                    setErrorMessage(null)
                    submitDataForm()
                    break
                }
        }
    }, [activeStep])

    const submitDataForm = () => {
        const isLlmGraph = aiQueryType === 'llm graph'

        // The advanced entity/stopword options only apply to the LLM graph mode.
        // For the ontology graph mode they are forced off.
        const effectiveModifyAnalyzedText = isLlmGraph
            ? modifyAnalyzedText
            : 'none'
        const effectiveReplaceEntities = isLlmGraph ? replaceEntities : false
        const effectiveHideSearchTerms = isLlmGraph ? hideSearchTerms : false

        const effectiveContextType = isLlmGraph
            ? effectiveModifyAnalyzedText == 'extractEntitiesOnly' ||
              effectiveModifyAnalyzedText == 'detectEntities'
                ? 'ONTOLOGY'
                : 'STANDARD'
            : 'ONTOLOGY'

        const dataToSend = {
            saveToGraphAndRedirect: true,
            contextName: selectedContext,
            aiQueryType: aiQueryType,
            mode: 'gptchat',
            modelToUse: modelToUse,
            prompt: [{ role: 'user', content: searchString }],
            numberOfResults: numberOfResults,
            receivedModel: 'default',
            excludesearchquery: effectiveHideSearchTerms ? '1' : '',
            modifyAnalyzedText: effectiveModifyAnalyzedText,
            replaceEntities: effectiveReplaceEntities,
            contextType: effectiveContextType,
        }

        const dataAsForm = qs.stringify(dataToSend)

        axios
            .post(`/graph/${username}/openai`, dataAsForm, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded', // Sending as URL-encoded form data
                },
            })
            .then(response => {
                if (response.data.redirectUrl) {
                    // If you're using window.location
                    window.location.href = response.data.redirectUrl

                    // Or, if you're using React Router's history (assuming you have access to the history object)
                    // history.push(response.data.redirectUrl);
                } else {
                    console.error(`Did't receive any response from the server.`)
                    if (response.data.error) {
                        setErrorMessage(
                            typeof response.data.error == 'object'
                                ? response.data.error.error ||
                                      JSON.stringify(response.data.error)
                                : response.data.error
                        )
                        setImportError(response.data.error)
                        setActiveStep(2)
                    }
                }
            })
            .catch(error => {
                // Handle the error here
                console.error('Received the following error', error)
                setErrorMessage(importErrorMessage(error))
                setImportError(response.data.error)
            })
    }

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth={true}
                maxWidth="lg"
            >
                <DialogTitle
                    sx={{
                        padding: 4,
                        color: theme => theme.palette.text.primary,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    {'Generate a knowledge graph using the AI'}
                    <IconButton
                        edge="end"
                        color="inherit"
                        onClick={onClose}
                        aria-label="close"
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <Stepper
                    activeStep={activeStep}
                    sx={{ paddingLeft: 4, paddingRight: 4, paddingTop: 2 }}
                >
                    {steps.map((label, index) => {
                        const stepProps = {}
                        const labelProps = {}

                        return (
                            <Step key={label} {...stepProps}>
                                <StepButton
                                    onClick={() => handleStep(index)}
                                    completed={(
                                        completedSteps.indexOf(index) >= 0
                                    ).toString()}
                                >
                                    <StepLabel
                                        {...labelProps}
                                        sx={{
                                            '& .MuiStepLabel-label': {
                                                fontSize: {
                                                    xs: '0.75rem',
                                                    sm: 'initial',
                                                },
                                            },
                                        }}
                                    >
                                        {label}
                                    </StepLabel>
                                </StepButton>
                            </Step>
                        )
                    })}
                </Stepper>

                {errorMessage && (
                    <Typography
                        sx={{
                            paddingRight: 4,
                            paddingLeft: 4,
                            paddingTop: 4,
                            paddingBottom: 0,
                        }}
                        color="error"
                        component="div"
                        gutterBottom
                    >
                        {errorMessage}
                    </Typography>
                )}

                {activeStep === 2 && (
                    <>
                        <DialogContent
                            sx={{
                                paddingRight: 4,
                                paddingLeft: 4,
                                paddingTop: 6,
                            }}
                        >
                            <DialogContentText>
                                Describe what you want to build a knowledge
                                graph for:
                            </DialogContentText>

                            <Grid container spacing={8}>
                                <Grid item xs={12} sm={7}>
                                    <FormControl
                                        fullWidth
                                        sx={{ marginTop: 2 }}
                                    >
                                        <TextField
                                            label={'Add a topic or a prompt'}
                                            value={searchString}
                                            variant="outlined"
                                            onChange={handleSearchStringChange}
                                            aria-describedby="searchString-helperText"
                                            multiline
                                            rows={4}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderWidth: 2,
                                                        borderColor:
                                                            'info.main',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderWidth: 2,
                                                        borderColor:
                                                            'info.dark',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderWidth: 2,
                                                    },
                                                    '& .MuiInputBase-inputMultiline': {
                                                        boxShadow:
                                                            'none !important',
                                                        border:
                                                            'none !important',
                                                    },
                                                },
                                            }}
                                        />

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                alignItems: 'center',
                                                gap: 1.5,
                                                mt: 2.5,
                                                ml: 2,
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ fontWeight: 500 }}
                                            >
                                                try one of these:
                                            </Typography>
                                            {[
                                                {
                                                    label:
                                                        'AI attention ontology',
                                                    value:
                                                        'build an ontology on AI attention mechanisms',

                                                    mode: 'ontology graph',
                                                },
                                                {
                                                    label: 'Body practices',
                                                    value:
                                                        'Generate a graph of various body practices related to embodiment, movement techniques, fitness, spirituality, martial arts, dance, etc.',
                                                    mode: 'llm graph',
                                                },
                                                {
                                                    label:
                                                        'Poets like Allen Ginsberg',
                                                    value:
                                                        'Generate a graph of poets, writers, and artists similar to Allen Ginsberg and their relations to each other',
                                                    mode: 'ontology graph',
                                                },
                                            ].map(suggestion => (
                                                <Chip
                                                    key={suggestion.label}
                                                    label={suggestion.label}
                                                    size="medium"
                                                    variant="outlined"
                                                    onClick={() => {
                                                        setAiQueryType(
                                                            suggestion.mode
                                                        )
                                                        setSearchString(
                                                            suggestion.value
                                                        )
                                                    }}
                                                    sx={{
                                                        cursor: 'pointer',
                                                        fontSize: '0.85rem',
                                                        fontWeight: 500,
                                                        borderColor:
                                                            'rgba(0, 200, 200, 0.5)',
                                                        color: 'info.main',
                                                        '&:hover': {
                                                            color: chipHoverAccent,
                                                            borderColor: chipHoverAccent,
                                                        },
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    </FormControl>
                                    <FormControl
                                        fullWidth
                                        sx={{ marginTop: 6 }}
                                    >
                                        <DialogContentText>
                                            Using the import app:
                                        </DialogContentText>
                                        <DialogContent
                                            sx={{
                                                pl: 0,
                                                ml: 0,
                                                marginTop: -2,
                                            }}
                                        >
                                            {renderImportOptionsMenu(
                                                filteredMenuItems,
                                                'aiGraphs'
                                            )}
                                        </DialogContent>
                                    </FormControl>
                                </Grid>
                                <Grid
                                    item
                                    xs={12}
                                    sm={4}
                                    sx={{
                                        marginTop: 0,
                                        marginBottom: 4,
                                    }}
                                >
                                    <FormControl
                                        fullWidth
                                        sx={{ marginTop: 2, marginBottom: 2 }}
                                    >
                                        <EditableSelect
                                            selectItems={contextNames}
                                            label={'Graph Name'}
                                            initialValue={selectedContext}
                                            onChange={name =>
                                                setSelectedContext(name)
                                            }
                                        />
                                    </FormControl>

                                    <FormControl
                                        fullWidth
                                        sx={{
                                            marginTop: 4,
                                            marginBottom: 2,
                                        }}
                                    >
                                        <InputLabel id="modelToUse-label">
                                            AI Model to Use
                                        </InputLabel>
                                        <Select
                                            labelId="modelToUse-label"
                                            id="modelToUse"
                                            value={modelToUse}
                                            onChange={event =>
                                                setModelToUse(
                                                    event.target.value
                                                )
                                            }
                                            label="AI Model to Use"
                                        >
                                            <MenuItem value={'gpt-4o'}>
                                                GPT-4o (creative)
                                            </MenuItem>
                                            <MenuItem value={'gpt-4o-mini'}>
                                                GPT-4o-mini (faster)
                                            </MenuItem>
                                            <MenuItem value={'gpt-5.4'}>
                                                Auto
                                            </MenuItem>
                                            <MenuItem value={'gpt-5.4-mini'}>
                                                GPT-5.4-mini (faster)
                                            </MenuItem>
                                            <MenuItem value={'gpt-5.6-terra'}>
                                                GPT-5.6 Terra
                                            </MenuItem>
                                            <MenuItem value={'gpt-5.6-sol'}>
                                                GPT-5.6 Sol
                                            </MenuItem>
                                            <MenuItem value={'gpt-6.1-terra'}>
                                                GPT-6.1 Terra
                                            </MenuItem>
                                            <MenuItem value={'gpt-6.1-sol'}>
                                                GPT-6.1 Sol
                                            </MenuItem>
                                            <MenuItem value={'gpt-4'}>
                                                GPT-4 (older but comprehensive)
                                            </MenuItem>
                                            <MenuItem value={'gpt-3.5-turbo'}>
                                                GPT-3.5 (Turbo)
                                            </MenuItem>
                                            <Divider sx={{ my: 0.5 }} />
                                            <MenuItem value={'claude-opus-5-5'}>
                                                Claude Opus 5.5 (most powerful)
                                            </MenuItem>
                                            <MenuItem value={'claude-sonnet-5-5'}>
                                                Claude Sonnet 5.5 (balanced)
                                            </MenuItem>
                                            <MenuItem value={'claude-fable-5-1'}>
                                                Claude Fable 5.1 (most advanced)
                                            </MenuItem>
                                            <Divider sx={{ my: 0.5 }} />
                                            <MenuItem
                                                value={'gemini-3.8-flash'}
                                            >
                                                Gemini 3.8 Flash
                                            </MenuItem>
                                            <MenuItem
                                                value={'gemini-3.5-flash-lite'}
                                            >
                                                Gemini 3.5 Flash Lite (faster)
                                            </MenuItem>
                                            <Divider sx={{ my: 0.5 }} />
                                            <MenuItem
                                                value={
                                                    'grok-4.7'
                                                }
                                            >
                                                Grok 4.7 (reasoning)
                                            </MenuItem>
                                            <MenuItem
                                                value={
                                                    'grok-4.20-0309-non-reasoning'
                                                }
                                            >
                                                Grok 4.20 (fast)
                                            </MenuItem>
                                        </Select>
                                    </FormControl>

                                    <FormControl
                                        fullWidth
                                        sx={{
                                            marginTop: 4,
                                            marginBottom: 2,
                                        }}
                                    >
                                        <InputLabel id="aiQueryType-label">
                                            Graph generation mode
                                        </InputLabel>
                                        <Select
                                            labelId="aiQueryType-label"
                                            id="aiQueryType"
                                            value={aiQueryType}
                                            onChange={event =>
                                                setAiQueryType(
                                                    event.target.value
                                                )
                                            }
                                            label="Graph generation mode"
                                        >
                                            <MenuItem
                                                value={'ontology graph'}
                                                key="ontology"
                                            >
                                                Knowledge Graph of Entities
                                            </MenuItem>
                                            <MenuItem
                                                value={'llm graph'}
                                                key="llm"
                                            >
                                                AI-Generated Statements
                                            </MenuItem>
                                        </Select>
                                        {aiQueryType == 'llm graph' && (
                                            <FormHelperText
                                                sx={{ marginTop: 1 }}
                                            >
                                                Good for general overview of a
                                                topic. Switch to "Knowledge
                                                Graph of Entities" for expert
                                                ontologies.
                                            </FormHelperText>
                                        )}
                                        {aiQueryType == 'ontology graph' && (
                                            <FormHelperText
                                                sx={{ marginTop: 1 }}
                                            >
                                                Suitable for LLM reasoning flows
                                                and advanced knowledge graphs.
                                                Switch to "AI Generated
                                                Statements" for LLM-generated
                                                overview of a topic.
                                            </FormHelperText>
                                        )}
                                    </FormControl>

                                    {aiQueryType == 'llm graph' && (
                                        <FormControl
                                            fullWidth
                                            sx={{
                                                marginTop: 2,
                                                marginBottom: 2,
                                            }}
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={
                                                            advancedSettings
                                                        }
                                                        onChange={event =>
                                                            setAdvancedSettings(
                                                                event.target
                                                                    .checked
                                                            )
                                                        }
                                                    />
                                                }
                                                label="Show advanced settings"
                                            />
                                        </FormControl>
                                    )}

                                    {aiQueryType == 'llm graph' &&
                                        advancedSettings && (
                                            <>
                                                <FormControl
                                                    fullWidth
                                                    sx={{
                                                        marginTop: 4,
                                                        marginBottom: 2,
                                                    }}
                                                >
                                                    <InputLabel id="modifyAnalyzedText-label">
                                                        Build a knowledge graph
                                                        from
                                                    </InputLabel>
                                                    <Select
                                                        labelId="modifyAnalyzedText-label"
                                                        id="modifyAnalyzedText"
                                                        value={
                                                            modifyAnalyzedText
                                                        }
                                                        onChange={event =>
                                                            setModifyAnalyzedText(
                                                                event.target
                                                                    .value
                                                            )
                                                        }
                                                        label="Build a knowledge graph from"
                                                    >
                                                        <MenuItem
                                                            value={'none'}
                                                            key="1"
                                                        >
                                                            the words used in
                                                            text
                                                        </MenuItem>
                                                        <MenuItem
                                                            value={
                                                                'detectEntities'
                                                            }
                                                            key="2"
                                                        >
                                                            the words and
                                                            detected entities
                                                        </MenuItem>
                                                        <MenuItem
                                                            value={
                                                                'extractEntitiesOnly'
                                                            }
                                                            key="3"
                                                        >
                                                            only the detected
                                                            entities
                                                        </MenuItem>
                                                    </Select>
                                                    {modifyAnalyzedText ==
                                                        'none' && (
                                                        <FormHelperText
                                                            sx={{
                                                                marginTop: 1,
                                                            }}
                                                        >
                                                            By default, we build
                                                            a graph from words,
                                                            not entities, as it
                                                            provides a richer
                                                            topical structure.
                                                        </FormHelperText>
                                                    )}
                                                    {modifyAnalyzedText !=
                                                        'none' && (
                                                        <FormHelperText
                                                            sx={{
                                                                marginTop: 1,
                                                            }}
                                                        >
                                                            You can also choose
                                                            to detect and tag
                                                            entities in your
                                                            text to build a
                                                            sparser graph.
                                                        </FormHelperText>
                                                    )}
                                                </FormControl>
                                                {modifyAnalyzedText !=
                                                    'none' && (
                                                    <FormControl
                                                        fullWidth
                                                        sx={{
                                                            marginTop: 2,
                                                            marginBottom: 2,
                                                        }}
                                                    >
                                                        <FormControlLabel
                                                            control={
                                                                <Switch
                                                                    id="replaceEntities"
                                                                    checked={
                                                                        replaceEntities
                                                                    }
                                                                    onChange={event =>
                                                                        setReplaceEntities(
                                                                            event
                                                                                .target
                                                                                .checked
                                                                        )
                                                                    }
                                                                />
                                                            }
                                                            label="Replace entities with root concepts"
                                                        />
                                                        {replaceEntities && (
                                                            <FormHelperText
                                                                id="replaceEntities-helperText"
                                                                sx={{
                                                                    marginTop: 1,
                                                                }}
                                                            >
                                                                When turned on,
                                                                we will not only
                                                                detect the
                                                                entities, but
                                                                also replace
                                                                them with their
                                                                ontological root
                                                                concepts
                                                                throughout your
                                                                text.
                                                            </FormHelperText>
                                                        )}
                                                    </FormControl>
                                                )}
                                                <FormControl
                                                    fullWidth
                                                    sx={{
                                                        marginTop: 4,
                                                        marginBottom: 2,
                                                    }}
                                                >
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={
                                                                    hideSearchTerms
                                                                }
                                                                onChange={event =>
                                                                    setHideSearchTerms(
                                                                        event
                                                                            .target
                                                                            .checked
                                                                    )
                                                                }
                                                            />
                                                        }
                                                        label="Hide Search Terms from Graph"
                                                    />

                                                    <FormHelperText>
                                                        explore the context
                                                        around the search query
                                                    </FormHelperText>
                                                </FormControl>
                                            </>
                                        )}
                                </Grid>
                            </Grid>
                        </DialogContent>
                    </>
                )}

                {activeStep === 3 && (
                    <ImportingNotice
                        linkHref={`/${username}/${selectedContext}/edit`}
                    />
                )}
                <DialogActions
                    sx={{
                        paddingRight: 4,
                        paddingLeft: 4,
                        paddingBottom: 4,
                        paddingTop: 2,
                    }}
                >
                    {/* <Button onClick={onClose} variant="outlined">
                        Cancel
                    </Button> */}
                    <Button
                        color="inherit"
                        variant="outlined"
                        disabled={activeStep === 0}
                        onClick={handleBack}
                        sx={{ mr: 1, px: 3, py: 1, fontSize: '0.95rem' }}
                    >
                        Back
                    </Button>

                    <LoadingButton
                        color="info"
                        variant="contained"
                        size="large"
                        endIcon={<ArrowForwardIcon />}
                        disabled={!searchString.length > 0 || activeStep == 3}
                        onClick={handleNext}
                        sx={{ px: 4, py: 1, fontSize: '0.95rem' }}
                    >
                        {activeStep === 2 ? 'Visualize' : 'Next'}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    )
}

function generatePresetContext(searchString) {
    const dateAndTime = new Date()
        .toISOString()
        .slice(2, 16)
        .replace(/-/g, '')
        .replace(/:/g, '')

    if (!searchString) return `ai_graph_${dateAndTime}`

    const twoFirstSearchQueryWords = slugifyString(
        searchString
            .split(' ')
            .slice(0, 2)
            .join('_')
            .slice(0, 20)
            .replace(/[^\P{L}a-z][^a-z]*/giu, '')
    )

    return `ai_graph_${twoFirstSearchQueryWords}`
}
